/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { ButtonIcon } from "../../../components/Button/Button"
import { faClipboardList, faClose, faFilePdf } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import GetSpace from "../../../utils/space"
import { useChecklist } from "../../../hooks/Checklist/useChecklist"
import type { ChecklistExecution } from "../../../Objects/Checklist/ChecklistExecution"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { formatDateTimeInTimeZone } from "../../../utils/timeZone"
import { describeChecklistExecutionStepValue } from "../../../Objects/Checklist/ChecklistExecutionStepFormat"

interface Props {
  id: number
  onClose: () => void
}

export const ChecklistExecutionDetailsPopup: React.FC<Props> = ({ id, onClose }) => {
  const { t, i18n } = useTranslation(["pages", "general"])

  const { getManagementExecution, downloadExecutionPdf, isLoading } = useChecklist()

  const [execution, setExecution] = useState<ChecklistExecution | null>(null)

  useEffect(() => {
    void (async () => {
      const result = await getManagementExecution(id)

      if (result) setExecution(result)
    })()
  }, [id])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={execution?.templateName ?? t("checklists.history.detailsTitle")}
              icon={faClipboardList}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faFilePdf}
                    disabled={isLoading || !execution}
                    onClick={() => downloadExecutionPdf(id)}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.checklist.checklistExecutionDetailsPopup.button.downloadPdf"
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.checklist.checklistExecutionDetailsPopup.button.onClose"
                  />
                </div>
              }
            />

            <div className="flex flex-col gap-2 p-2 max-h-[70vh] overflow-y-auto">
              {execution?.steps.map((step) => (
                <Animate type={AnimationTypes.fadeInTraslate} key={step.id} duration="1.5s">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">{step.title}</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-semibold ${
                          step.status === "Completed"
                            ? "bg-green-100 text-green-700"
                            : step.status === "NotCompleted"
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {t(`checklists.history.stepStatus${step.status}`)}
                      </span>
                    </div>

                    <p className="text-sm text-slate-600">
                      {describeChecklistExecutionStepValue(step, t, t("checklists.history.stepStatusCompleted"))}
                    </p>

                    {step.notes && <p className="text-xs text-slate-500 italic">{step.notes}</p>}

                    {step.completedByEmployeeName && step.completedAtUtc && (
                      <p className="text-xs text-slate-400">
                        {step.completedByEmployeeName} ·{" "}
                        {formatDateTimeInTimeZone(step.completedAtUtc, i18n.language, companyMemory.timeZoneId)}
                      </p>
                    )}
                  </div>
                </Animate>
              ))}
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
